<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageTimestamp } from 'shared/helpers/timeHelper';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

const {
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
  isAStudioChannel,
} = useInbox();

const {
  status,
  isPrivate,
  createdAt,
  sourceId,
  messageType,
  contentType,
  contentAttributes,
  additionalAttributes,
  webchatKind,
} = useMessageContext();

const { t } = useI18n();

// Webchat de continuidade: saiu pra tela do cliente, mas ele não estava
// conectado — fica `sent` com este carimbo até ele abrir (não é falha).
// Pendente só enquanto `sent`: quando o cliente abre a tela, o status vira
// delivered/read e o carimbo antigo não pode mais dizer "aguardando".
// As chaves chegam camelizadas em parte dos caminhos (ver sentFromPhone):
// aceita as duas grafias.
const webchatFlag = key =>
  additionalAttributes.value?.[key] === true ||
  additionalAttributes.value?.[
    key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
  ] === true;

const isWebchatPending = computed(
  () => webchatFlag('webchat_pending') && status.value === MESSAGE_STATUS.SENT
);

// Trafegou pela TELA do webchat (`messages.webchat_kind`, coluna própria — o
// antigo carimbo em additional_attributes não existe mais): nunca passou pela
// Meta, nunca terá source_id. Os ticks saem só do status (o
// Webchat::SendOnWebchatService e o last_seen da tela é que o movem).
const isWebchatScreen = computed(() => webchatKind.value === 'screen');
// A direção já decide: do cliente = recebida, nossa = enviada.
const webchatScreenTooltip = computed(() =>
  messageType.value === MESSAGE_TYPES.INCOMING
    ? t('CONVERSATION.WEBCHAT.MESSAGE_SCREEN_IN_TOOLTIP')
    : t('CONVERSATION.WEBCHAT.MESSAGE_SCREEN_OUT_TOOLTIP')
);

// O convite/reenvio do link: mensagem PAGA no WhatsApp, não parte da conversa
// na tela. Vale dizer isso na linha de meta — é dinheiro do provedor.
const isWebchatLink = computed(() => webchatKind.value === 'link');

// Cliente ABRIU o link (`webchat_opened_at`, carimbado no exchange!). Vale pro
// template de campanha com botão do webchat, que não passa pelo bubble CtaUrl.
const webchatOpenedAt = computed(() => {
  const value =
    contentAttributes.value?.webchat_opened_at ??
    contentAttributes.value?.webchatOpenedAt;
  return value ? Math.floor(new Date(value).getTime() / 1000) : null;
});
const webchatLinkLabel = computed(() =>
  webchatOpenedAt.value
    ? t('CONVERSATION.WEBCHAT.LINK_OPENED', {
        time: messageTimestamp(webchatOpenedAt.value, 'HH:mm'),
      })
    : t('CONVERSATION.WEBCHAT.MESSAGE_LINK')
);

const readableTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, h:mm:ss a')
);

const isSentFromPhone = computed(
  () =>
    additionalAttributes.value?.sentFromPhone === true ||
    additionalAttributes.value?.sent_from_phone === true
);

const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  // Channel::Studio é o inbox de simulação do playground — não há entrega real,
  // então `statusToShow` cairia eternamente em PROGRESS ("enviando"). Suprime.
  if (isAStudioChannel.value) return false;
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  if (contentAttributes.value?.deleted) return false;
  // Messages sent from the WhatsApp Business phone app (coexistence echoes)
  // have no delivery/read updates from Meta — skip ticks entirely.
  if (isSentFromPhone.value) return false;

  // Voice transcripts are display-only, no delivery status
  if (
    contentType.value === 'voice_transcript' ||
    contentAttributes.value?.voice_transcript
  ) {
    return false;
  }

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;
  if (isWebchatScreen.value) return status.value === MESSAGE_STATUS.SENT;

  if (isAnEmailChannel.value) {
    return !!sourceId.value;
  }

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;
  if (isWebchatScreen.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }

  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;
  if (isWebchatScreen.value) return status.value === MESSAGE_STATUS.READ;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value
  ) {
    const result = sourceId.value && status.value === MESSAGE_STATUS.READ;

    return result;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

const statusToShow = computed(() => {
  const result = (() => {
    if (isRead.value) return MESSAGE_STATUS.READ;
    if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
    if (isSent.value) return MESSAGE_STATUS.SENT;
    return MESSAGE_STATUS.PROGRESS;
  })();

  return result;
});
</script>

<template>
  <div class="text-xs flex items-center gap-1.5">
    <div class="inline">
      <time class="inline">{{ readableTime }}</time>
    </div>
    <Icon v-if="isPrivate" icon="i-lucide-lock-keyhole" class="size-3" />
    <span
      v-if="isWebchatPending"
      v-tooltip="t('CONVERSATION.WEBCHAT.PENDING_TOOLTIP')"
      class="inline-flex items-center gap-1 text-n-amber-11"
    >
      <Icon icon="i-lucide-clock" class="size-3" />
      {{ t('CONVERSATION.WEBCHAT.PENDING') }}
    </span>
    <span
      v-if="isWebchatScreen"
      v-tooltip="webchatScreenTooltip"
      class="inline-flex items-center text-n-slate-11"
      :aria-label="webchatScreenTooltip"
    >
      <Icon icon="i-lucide-link" class="size-3" />
    </span>
    <span
      v-else-if="isWebchatLink"
      v-tooltip="t('CONVERSATION.WEBCHAT.MESSAGE_LINK_TOOLTIP')"
      class="inline-flex items-center gap-1"
      :class="webchatOpenedAt ? 'text-n-green-11' : 'text-n-slate-11'"
    >
      <Icon
        :icon="
          webchatOpenedAt ? 'i-lucide-check-circle' : 'i-lucide-send-horizontal'
        "
        class="size-3"
      />
      {{ webchatLinkLabel }}
    </span>
    <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
  </div>
</template>
